import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validateResponse } from "@/api/validateResponse";
import {
  PortalCheckoutSchema,
  PortalContractSchema,
  PortalMeSchema,
  type PortalContract,
  type PortalMe,
  type PortalSignInput,
} from "./types";

/**
 * Dedicated axios instance for the public client portal.
 *
 * Deliberately NOT `@/api/client`'s `apiClient`: that instance's response
 * interceptor clears the staff session and redirects to /login on any 401.
 * A portal visitor's expired/invalid token is an EXPECTED, customer-facing
 * outcome (see PortalPage's invalid-link state) — never a staff auth
 * failure, so it must never trigger that redirect. No cookies either: the
 * portal token travels as an explicit `?token=` query param on every call
 * (matches app/api/deps.py::verify_portal_token, which reads `token` or a
 * Bearer header — this app always uses the query param).
 */
const portalClient = axios.create({
  baseURL: "/api/public/portal",
  headers: { "Content-Type": "application/json" },
});

export const portalKeys = {
  me: (token: string | null) => ["portal", "me", token] as const,
};

/**
 * GET /me. Intentionally `enabled: !!token` — with no token in the URL at
 * all there is nothing valid to ask the backend, so PortalPage renders the
 * "invalid or expired link" state synchronously without ever calling the
 * API (see PortalPage.tsx). When a token IS present but rejected, the
 * backend returns 401 and this query's `error` drives the same UI state.
 */
export function usePortalMe(token: string | null) {
  return useQuery<PortalMe>({
    queryKey: portalKeys.me(token),
    queryFn: async () => {
      const { data } = await portalClient.get("/me", { params: { token } });
      return validateResponse(PortalMeSchema, data, "GET /public/portal/me");
    },
    enabled: !!token,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function useSignPortalContract(token: string | null) {
  const queryClient = useQueryClient();
  return useMutation<PortalContract, unknown, PortalSignInput>({
    mutationFn: async (input) => {
      const { data } = await portalClient.post("/contract/sign", input, {
        params: { token },
      });
      return validateResponse(
        PortalContractSchema,
        data,
        "POST /public/portal/contract/sign",
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.me(token) });
    },
  });
}

/** Returns the Stripe Checkout URL; caller does the `window.location.href` redirect. */
export function usePortalInvoiceCheckout(token: string | null) {
  return useMutation<string, unknown, string>({
    mutationFn: async (invoiceId) => {
      const { data } = await portalClient.post(
        `/invoice/${invoiceId}/checkout`,
        {},
        { params: { token } },
      );
      return validateResponse(
        PortalCheckoutSchema,
        data,
        "POST /public/portal/invoice/:id/checkout",
      ).url;
    },
  });
}
